import router from '@/router';
import { clearLocalSession } from '@/helpers/auth';
import { updateTokens } from '@/shared/common/utils/token.util';
import { LAST_ACTIVITY_TIMESTAMP_KEY } from '@/shared/common/constants/auth.constant';
import type { BroadcastMessage } from '@/shared/common/types/sessionBroadcast.type';

const tabId = generateTabId();

/**
 * Genera un ID único para la pestaña.
 * Usa crypto.randomUUID si está disponible (contextos seguros),
 * o un fallback basado en Math.random para compatibilidad.
 */
function generateTabId(): string {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    return window.crypto.randomUUID();
  }
}

/**
 * Maneja el cierre de otras pestañas.
 * @param message Mensaje recibido del broadcast
 * @param onLogoutCallback Callback opcional para ejecutar antes del logout
 * @returns void
 */
export function handleCloseOtherTabs(
  message: BroadcastMessage,
  onLogoutCallback: (() => void) | null
): void {
  if (message.senderId !== tabId) {
    if (onLogoutCallback) {
      onLogoutCallback();
    }
    clearLocalSession();
    router.replace({ name: 'log-in' });
  }
}

/**
 * Maneja el cierre de todas las pestañas.
 * @param onLogoutCallback Callback opcional para ejecutar antes del logout
 * @returns void
 */
export function handleCloseAllTabs(onLogoutCallback: (() => void) | null): void {
  if (onLogoutCallback) {
    onLogoutCallback();
  }
  clearLocalSession();
  router.replace({ name: 'log-in' });
}

/**
 * Maneja la actualización de tokens cuando otra pestaña los refresca.
 * @param message Mensaje recibido con los nuevos tokens
 */
export function handleTokenRefreshed(message: BroadcastMessage): void {
  if (message.senderId !== tabId && message.tokenData) {
    updateTokens({
      idToken: message.tokenData.idToken,
      refreshToken: message.tokenData.refreshToken,
      expiresIn: message.tokenData.expiresIn,
    });
  }
}

/**
 * Maneja la notificación de actividad de usuario en otra pestaña.
 * @param message Mensaje con el timestamp de actividad
 * @param onUserActivityCallback Callback opcional para notificar al componente
 */
export function handleUserActivity(
  message: BroadcastMessage,
  onUserActivityCallback: ((timestamp: number) => void) | null
): void {
  if (message.senderId !== tabId && message.activityTimestamp) {
    localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, message.activityTimestamp.toString());
    if (onUserActivityCallback) {
      onUserActivityCallback(message.activityTimestamp);
    }
  }
}

/**
 * Maneja la visualización de la alerta de expiración iniciada por otra pestaña.
 * @param message Mensaje con el tiempo restante
 * @param onExpirationAlertShownCallback Callback opcional para mostrar la alerta
 */
export function handleExpirationAlertShown(
  message: BroadcastMessage,
  onExpirationAlertShownCallback: ((timeRemaining: number) => void) | null
): void {
  if (message.senderId !== tabId && message.timeRemaining !== undefined) {
    if (onExpirationAlertShownCallback) {
      onExpirationAlertShownCallback(message.timeRemaining);
    }
  }
}

/**
 * Maneja el ocultamiento de la alerta de expiración iniciado por otra pestaña.
 * @param message Mensaje de ocultar alerta
 * @param onExpirationAlertHiddenCallback Callback opcional para ocultar la alerta
 */
export function handleExpirationAlertHidden(
  message: BroadcastMessage,
  onExpirationAlertHiddenCallback: (() => void) | null
): void {
  if (message.senderId !== tabId) {
    if (onExpirationAlertHiddenCallback) {
      onExpirationAlertHiddenCallback();
    }
  }
}

/**
 * Obtiene el ID único de la pestaña actual.
 * @returns ID de la pestaña
 */
export function getTabId(): string {
  return tabId;
}
